import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';

interface MedicalRecord {
  id?: string;
  hasPreviewHeartCondition?: boolean;
  isPregnant?: boolean;
  cardiovascularConditions: string[];
  metabolicConditions: string[];
  respiratoryConditions: string[];
  musculoskeletalConditions: string[];
  otherConditions: string[];
  thyroidConditions: string[];
  symptomsAndConditions: Record<string, boolean>;
  physicalActivities: string[];
  additionalNotes?: string;
  signatureData?: string;
  signatureDate?: string;
}

export const useClientMedicalRecord = (clientId: string) => {
  const { currentOrganization } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: medicalRecord, isLoading } = useQuery({
    queryKey: ['client-medical-record', clientId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('client_medical_records')
        .select('*')
        .eq('client_id', clientId)
        .eq('organization_id', currentOrganization?.id)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!clientId && !!currentOrganization?.id,
  });

  const saveMutation = useMutation({
    mutationFn: async (record: MedicalRecord) => {
      const recordData = {
        client_id: clientId,
        organization_id: currentOrganization?.id,
        has_previous_heart_condition: record.hasPreviewHeartCondition,
        is_pregnant: record.isPregnant,
        cardiovascular_conditions: record.cardiovascularConditions,
        metabolic_conditions: record.metabolicConditions,
        respiratory_conditions: record.respiratoryConditions,
        musculoskeletal_conditions: record.musculoskeletalConditions,
        other_conditions: record.otherConditions,
        thyroid_conditions: record.thyroidConditions,
        symptoms_and_conditions: record.symptomsAndConditions,
        physical_activities: record.physicalActivities,
        additional_notes: record.additionalNotes,
        signature_data: record.signatureData,
        signature_date: record.signatureDate ? new Date(record.signatureDate).toISOString() : null,
        updated_by: (await supabase.auth.getUser()).data.user?.id,
      };

      if (medicalRecord?.id) {
        const { data, error } = await supabase
          .from('client_medical_records')
          .update(recordData)
          .eq('id', medicalRecord.id)
          .select()
          .single();
        
        if (error) throw error;
        return data;
      } else {
        const { data, error } = await supabase
          .from('client_medical_records')
          .insert({
            ...recordData,
            created_by: (await supabase.auth.getUser()).data.user?.id,
          })
          .select()
          .single();
        
        if (error) throw error;
        return data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-medical-record', clientId] });
    },
    onError: (error) => {
      toast({
        title: "Σφάλμα",
        description: "Αποτυχία αποθήκευσης ιατρικού αρχείου",
        variant: "destructive",
      });
    },
  });

  return {
    medicalRecord,
    isLoading,
    saveMedicalRecord: saveMutation.mutateAsync,
    isSaving: saveMutation.isPending,
  };
};